/**
 * 实时积分预估
 *
 * 根据当前选择的 modelId / mode / duration / resolution / n，
 * 以及音频开关与参考素材（视频总时长 / 参考图张数）调用后端预算接口。
 * 与实际扣费走同一套公式（后端同口径），保证预览价 = 实扣价。
 * debounce 300ms，避免用户拖动时疯狂请求。
 */

import { useEffect, useRef, useState } from 'react';
import { Coins, Loader2 } from 'lucide-react';
import { videoApi, type VideoCreditsPreviewBody, type VideoCreditsBreakdown } from '../services/videoApi';

interface VideoCreditsPreviewProps {
  modelId: string;
  mode: VideoCreditsPreviewBody['mode'];
  duration: number;
  resolution: string;
  n: number;
  /** 是否开启音频生成（乘音频倍率） */
  enableAudio?: boolean;
  /** 参考视频总时长（秒，输入侧计费） */
  refVideoDurationSec?: number;
  /** 参考图张数（含首尾帧，输入侧计费） */
  refImageCount?: number;
  /** 用户当前积分，用于显示余额是否够 */
  userCredits?: number;
}

export function VideoCreditsPreview({
  modelId,
  mode,
  duration,
  resolution,
  n,
  enableAudio,
  refVideoDurationSec,
  refImageCount,
  userCredits,
}: VideoCreditsPreviewProps) {
  const [creditsPerJob, setCreditsPerJob] = useState<number | null>(null);
  const [creditsTotal, setCreditsTotal] = useState<number | null>(null);
  const [breakdown, setBreakdown] = useState<VideoCreditsBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastReqIdRef = useRef(0);

  useEffect(() => {
    if (!modelId || !mode || !duration || !resolution) {
      setCreditsPerJob(null);
      setCreditsTotal(null);
      setBreakdown(null);
      return;
    }

    const reqId = ++lastReqIdRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await videoApi.previewCredits({
          modelId,
          mode,
          duration,
          resolution,
          n,
          enableAudio,
          refVideoDurationSec,
          refImageCount,
        });
        if (reqId !== lastReqIdRef.current) return; // 过期请求结果丢弃
        setCreditsPerJob(data.creditsPerJob);
        setCreditsTotal(data.creditsTotal);
        setBreakdown(data.breakdown ?? null);
      } catch (err) {
        if (reqId !== lastReqIdRef.current) return;
        setError(err instanceof Error ? err.message : '预算积分失败');
        setCreditsPerJob(null);
        setCreditsTotal(null);
        setBreakdown(null);
      } finally {
        if (reqId === lastReqIdRef.current) setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [modelId, mode, duration, resolution, n, enableAudio, refVideoDurationSec, refImageCount]);

  const insufficient = userCredits != null && creditsTotal != null && userCredits < creditsTotal;

  // 输入素材费用明细：参考视频 + 参考图（>0 才展示，避免干扰纯文生场景）
  const inputCredits =
    breakdown != null ? breakdown.refVideo + breakdown.refImage : 0;

  return (
    <div
      className={`flex flex-col gap-1 px-3 py-2.5 rounded-xl border ${
        insufficient
          ? 'border-rose-200 bg-rose-50 text-rose-700'
          : 'border-light-border bg-white text-light-text'
      }`}
    >
      <div className="flex items-center gap-2">
        <Coins className="w-4 h-4" />
        {loading ? (
          <span className="flex items-center gap-1 text-sm text-light-muted">
            <Loader2 className="w-3 h-3 animate-spin" />
            计算中
          </span>
        ) : error ? (
          <span className="text-sm text-rose-600">{error}</span>
        ) : creditsTotal != null && creditsPerJob != null ? (
          <span className="text-sm">
            本次将扣除
            <span className={`mx-1 font-semibold ${insufficient ? 'text-rose-700' : 'text-accent-blue'}`}>
              {creditsTotal}
            </span>
            积分
            {n > 1 && (
              <span className="text-light-muted ml-1 text-xs">
                ({n} × {creditsPerJob})
              </span>
            )}
            {insufficient && (
              <span className="ml-2 text-xs text-rose-600">积分不足</span>
            )}
          </span>
        ) : (
          <span className="text-sm text-light-muted">选择参数后预估积分</span>
        )}
      </div>
      {breakdown != null && inputCredits > 0 && (
        <p className="pl-6 text-xs text-light-muted">
          含生成 {breakdown.output} + 参考素材 {inputCredits} 积分
        </p>
      )}
    </div>
  );
}
